import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, CheckCircle2, ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Logo } from "@/components/common/Logo";
import api, { formatApiErrorDetail } from "@/lib/api";

const COPY = {
  ca: { title: "Restablir contrasenya", subtitle: "Escull una contrasenya nova per al teu compte.",
        password: "Nova contrasenya", confirm: "Repeteix la contrasenya", submit: "Guarda la contrasenya",
        min: "Mínim 8 caràcters.", nomatch: "Les contrasenyes no coincideixen.",
        notoken: "L'enllaç no és vàlid o ha caducat. Torna a sol·licitar-ne un de nou.",
        done: "Contrasenya actualitzada!", doneSub: "Ja pots iniciar sessió amb la nova contrasenya.",
        goLogin: "Anar a iniciar sessió" },
  es: { title: "Restablecer contraseña", subtitle: "Elige una contraseña nueva para tu cuenta.",
        password: "Nueva contraseña", confirm: "Repite la contraseña", submit: "Guardar contraseña",
        min: "Mínimo 8 caracteres.", nomatch: "Las contraseñas no coinciden.",
        notoken: "El enlace no es válido o ha caducado. Solicita uno nuevo.",
        done: "¡Contraseña actualizada!", doneSub: "Ya puedes iniciar sesión con la nueva contraseña.",
        goLogin: "Ir a iniciar sesión" },
  en: { title: "Reset password", subtitle: "Choose a new password for your account.",
        password: "New password", confirm: "Repeat password", submit: "Save password",
        min: "Minimum 8 characters.", nomatch: "Passwords do not match.",
        notoken: "The link is invalid or has expired. Please request a new one.",
        done: "Password updated!", doneSub: "You can now sign in with the new password.",
        goLogin: "Go to sign in" },
  fr: { title: "Réinitialiser le mot de passe", subtitle: "Choisissez un nouveau mot de passe.",
        password: "Nouveau mot de passe", confirm: "Répétez le mot de passe", submit: "Enregistrer",
        min: "Minimum 8 caractères.", nomatch: "Les mots de passe ne correspondent pas.",
        notoken: "Le lien n'est pas valide ou a expiré. Demandez-en un nouveau.",
        done: "Mot de passe mis à jour !", doneSub: "Vous pouvez maintenant vous connecter.",
        goLogin: "Se connecter" },
};

export default function ResetPassword() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const c = COPY[lang] || COPY.ca;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useSEO({ lang, title: `${c.title} | HO-SYS`, description: c.subtitle, path: "/reset-password", noindex: true });

  const inputCls = "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-neon/60 transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError(c.min);
    if (password !== confirm) return setError(c.nomatch);
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || c.notoken);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-neon/15 blur-[120px] rounded-full" />
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><Link to={`/${lang}`}><Logo variant="full" height={160} /></Link></div>

        {done ? (
          <div data-testid="reset-done" className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-xl p-8 text-center">
            <div className="flex justify-center mb-4"><CheckCircle2 className="w-14 h-14 text-neon" /></div>
            <h1 className="font-display text-2xl font-bold text-white">{c.done}</h1>
            <p className="text-white/60 text-sm mt-2 mb-7">{c.doneSub}</p>
            <button data-testid="reset-go-login" onClick={() => navigate(`/${lang}/login`)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-neon px-6 py-3.5 font-semibold text-black hover:bg-white transition-colors">
              {c.goLogin} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form" className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-xl p-8">
            <div className="flex justify-center mb-3"><div className="p-3 rounded-full bg-neon/[0.08] border border-neon/30"><KeyRound className="w-5 h-5 text-neon" /></div></div>
            <h1 className="font-display text-2xl font-bold text-white text-center">{c.title}</h1>
            <p className="text-white/50 text-sm text-center mt-1 mb-7">{c.subtitle}</p>

            {!token && (
              <p data-testid="reset-notoken" className="mb-4 text-sm text-red-400 text-center">{c.notoken}</p>
            )}

            <div className="space-y-4">
              <input data-testid="reset-password" type="password" required placeholder={c.password}
                value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} disabled={!token} />
              <input data-testid="reset-confirm" type="password" required placeholder={c.confirm}
                value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} disabled={!token} />
            </div>
            {error && <p data-testid="reset-error" className="mt-4 text-sm text-red-400">{error}</p>}
            <button data-testid="reset-submit" type="submit" disabled={loading || !token}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-neon px-6 py-3.5 font-semibold text-black hover:bg-white transition-colors disabled:opacity-60">
              {loading ? "..." : c.submit}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
